import { useEffect, useRef, useState } from "react";
import { getUserAccess } from "@/api/user-access-api.js";
import { isRequestAborted } from "@/api/api-utils.js";
import { useSessionTenant } from "@/contexts/SessionTenantContext.jsx";

/**
 * @typedef {Object} TenantAccess
 * @property {boolean} canRunRcaAi
 * @property {boolean} showRcaAi
 * @property {boolean} isOneViewAdmin
 */

/** @type {Map<number, TenantAccess>} */
const accessByTenantId = new Map();

/**
 * @param {number|null|undefined} tenantId
 * @returns {{ canRunRcaAi: boolean, showRcaAi: boolean, isOneViewAdmin: boolean, isLoading: boolean }}
 */
function readAccessState(tenantId) {
  if (tenantId == null) {
    return {
      canRunRcaAi: false,
      showRcaAi: true,
      isOneViewAdmin: false,
      isLoading: false,
    };
  }
  const cached = accessByTenantId.get(tenantId);
  if (cached !== undefined) {
    return { ...cached, isLoading: false };
  }
  return {
    canRunRcaAi: false,
    showRcaAi: false,
    isOneViewAdmin: false,
    isLoading: true,
  };
}

/**
 * Lazy, tenant-scoped user access for the current session tenant.
 * Fetches only when mounted; caches per tenant for the browser session.
 *
 * @returns {{ canRunRcaAi: boolean, showRcaAi: boolean, isOneViewAdmin: boolean, isLoading: boolean }}
 */
export function useUserAccess() {
  const { currentTenantId, tenantRefreshKey } = useSessionTenant();
  const tenantRefreshKeyRef = useRef(tenantRefreshKey);
  tenantRefreshKeyRef.current = tenantRefreshKey;

  const [state, setState] = useState(() => readAccessState(currentTenantId));

  useEffect(() => {
    if (currentTenantId == null) {
      setState({
        canRunRcaAi: false,
        showRcaAi: true,
        isOneViewAdmin: false,
        isLoading: false,
      });
      return;
    }

    const cached = accessByTenantId.get(currentTenantId);
    if (cached !== undefined) {
      setState({ ...cached, isLoading: false });
      return;
    }

    let cancelled = false;
    const refreshKeyAtStart = tenantRefreshKeyRef.current;

    const load = async () => {
      setState({
        canRunRcaAi: false,
        showRcaAi: false,
        isOneViewAdmin: false,
        isLoading: true,
      });
      try {
        const { canRunRcaAi, showRcaAi, isOneViewAdmin } = await getUserAccess();
        if (cancelled || refreshKeyAtStart !== tenantRefreshKeyRef.current) {
          return;
        }
        const access = { canRunRcaAi, showRcaAi, isOneViewAdmin };
        accessByTenantId.set(currentTenantId, access);
        setState({ ...access, isLoading: false });
      } catch (err) {
        if (cancelled || isRequestAborted(err)) return;
        console.error("getUserAccess failed:", err);
        if (refreshKeyAtStart !== tenantRefreshKeyRef.current) return;
        setState({
          canRunRcaAi: false,
          showRcaAi: true,
          isOneViewAdmin: false,
          isLoading: false,
        });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [currentTenantId, tenantRefreshKey]);

  return state;
}
